import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Shield, Server, Globe2, ArrowRight, Facebook, Twitter, Linkedin, Instagram, Youtube, Lock, Zap, CheckCircle2 } from 'lucide-react';
import Logo from '../base/Logo';

const SOCIALS = [
  { Icon: Facebook, href: '#', label: 'Facebook' },
  { Icon: Twitter, href: '#', label: 'Twitter / X' },
  { Icon: Linkedin, href: '#', label: 'LinkedIn' },
  { Icon: Instagram, href: '#', label: 'Instagram' },
  { Icon: Youtube, href: '#', label: 'YouTube' },
];

const CERTIFICATIONS = [
  { icon: '🔒', label: 'ISO 27001', sub: 'Məlumat Təhlükəsizliyi' },
  { icon: '🇦🇿', label: 'AZ Dövlət', sub: 'Standart uyğunluğu' },
  { icon: '🛡️', label: 'GDPR Ready', sub: 'Tam uyğun' },
];

export default function Footer() {
  const { t } = useTranslation();

  const columns = [
    {
      title: t('footer.product'),
      links: [
        { label: t('footer.pricing_link'), to: '/pricing' },
        { label: t('footer.features'), to: '/#features' },
        { label: t('footer.security'), to: '/#security' },
        { label: 'Email Hosting', to: '/#hosting' },
        { label: 'Korporativ Paket', to: '/pricing' },
        { label: 'API Girişi', to: '/#api' },
      ],
    },
    {
      title: t('footer.company'),
      links: [
        { label: t('footer.about'), to: '/#about' },
        { label: t('footer.blog'), to: '/#blog' },
        { label: t('footer.careers'), to: '/#careers' },
        { label: 'Press Kit', to: '/#press' },
        { label: 'Tərəfdaşlar', to: '/#partners' },
        { label: 'Xəbərlər', to: '/#news' },
      ],
    },
    {
      title: t('footer.help'),
      links: [
        { label: t('footer.support_link'), to: '/support' },
        { label: t('footer.contact'), to: '/support' },
        { label: t('footer.docs'), to: '/#docs' },
        { label: 'Video Dərslər', to: '/#tutorials' },
        { label: 'Status Səhifəsi', to: '/#status' },
        { label: 'Bildiriş Abunəsi', to: '/#alerts' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.terms'), to: '/#terms' },
        { label: t('footer.privacy'), to: '/#privacy' },
        { label: t('footer.gdpr'), to: '/#gdpr' },
        { label: 'Cookie Siyasəti', to: '/#cookies' },
        { label: 'SLA Müqaviləsi', to: '/#sla' },
        { label: 'Şikayət Forması', to: '/#complaint' },
      ],
    },
  ];

  return (
    <footer className="bg-slate-950 text-slate-400">

      {/* Newsletter banner */}
      <div className="border-b border-slate-800/60 bg-gradient-to-r from-primary-950/80 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white font-display font-bold text-xl mb-1">Xəbərdar olun</h3>
              <p className="text-slate-500 text-sm">Yeni xüsusiyyətlər, təhlükəsizlik yeniləmələri və kampaniyalardan ilk siz xəbər tutun.</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <input
                type="email"
                placeholder="email@sizin.az"
                className="flex-1 md:w-64 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30"
              />
              <button className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors flex-shrink-0">
                Abunə ol <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Brand block */}
          <div className="lg:col-span-4">
            <Logo variant="light" size="lg" />
            <p className="mt-4 text-slate-500 text-sm leading-relaxed max-w-sm">
              {t('footer.desc')}
            </p>

            {/* Contact */}
            <div className="mt-6 space-y-2.5">
              <div className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-slate-300 transition-colors cursor-default">
                <Mail size={13} className="text-primary-600 flex-shrink-0" />
                <span>support@emailaze.com</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-slate-300 transition-colors cursor-default">
                <Phone size={13} className="text-primary-600 flex-shrink-0" />
                <span>+994 12 555 00 11</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-slate-500 cursor-default">
                <MapPin size={13} className="text-primary-600 flex-shrink-0" />
                <span>Bakı şəh., İnşaatçılar pr. 14, AZ1065</span>
              </div>
            </div>

            {/* Social links */}
            <div className="mt-6 flex items-center gap-2">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-primary-600 border border-slate-700 hover:border-primary-500 flex items-center justify-center text-slate-500 hover:text-white transition-all"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>

            {/* Server status mini-cards */}
            <div className="mt-6 grid grid-cols-3 gap-2">
              {[
                { Icon: Server, label: 'DC-1', status: 'Online', color: 'text-emerald-500' },
                { Icon: Lock, label: 'SSL', status: 'Active', color: 'text-emerald-500' },
                { Icon: Zap, label: 'CDN', status: '12ms', color: 'text-amber-400' },
              ].map(({ Icon, label, status, color }) => (
                <div key={label} className="bg-slate-900 border border-slate-800 rounded-lg px-2 py-2 text-center">
                  <Icon size={12} className="text-slate-600 mx-auto mb-1" />
                  <div className="text-slate-500 text-xs font-mono leading-none">{label}</div>
                  <div className={`${color} text-xs font-semibold mt-0.5`}>{status}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {columns.map((col) => (
              <div key={col.title}>
                <h3 className="text-slate-200 text-xs font-bold tracking-widest uppercase mb-5 pb-2 border-b border-slate-800">
                  {col.title}
                </h3>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-slate-500 hover:text-slate-200 transition-colors flex items-center gap-1.5 group"
                      >
                        <span className="w-1 h-1 rounded-full bg-slate-700 group-hover:bg-primary-500 transition-colors flex-shrink-0" />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-12 pt-8 border-t border-slate-800/60">
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <span className="text-slate-600 text-xs uppercase tracking-widest font-semibold">Sertifikatlar və uyğunluq:</span>
            {CERTIFICATIONS.map(({ icon, label, sub }) => (
              <div key={label} className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2">
                <span className="text-base">{icon}</span>
                <div>
                  <div className="text-slate-300 text-xs font-semibold leading-none">{label}</div>
                  <div className="text-slate-600 text-xs leading-none mt-0.5">{sub}</div>
                </div>
                <CheckCircle2 size={12} className="text-emerald-500 ml-1" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800/40 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600">{t('footer.copyright')}</p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/#terms" className="hover:text-slate-400 transition-colors">{t('footer.terms')}</Link>
            <span className="text-slate-800">·</span>
            <Link to="/#privacy" className="hover:text-slate-400 transition-colors">{t('footer.privacy')}</Link>
            <span className="text-slate-800">·</span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-700">Bütün sistemlər aktiv</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
